import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Database, Search, RefreshCw, Zap, Car, Image, Settings, 
  ChevronRight, Loader2, CheckCircle, AlertCircle, Play, Square,
  Trash2, Edit, Eye, Download, Upload, BarChart3, Clock, Filter,
  Thermometer, Wrench, MapPin, Battery, Cpu, Shield, Wrench as Tool,
  XCircle, StopCircle, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VehicleDetailView from '../../components/admin/VehicleDetailView';
import { VehicleCascadingDropdown } from '../../components/VehicleCascadingDropdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminVehicleMaster = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Cascading Dropdown State
  const [searchMode, setSearchMode] = useState('dropdown'); // 'dropdown' or 'text'
  const [dropdownSelection, setDropdownSelection] = useState(null);
  
  // Filters
  const [filterBrand, setFilterBrand] = useState('');
  const [filterHasImage, setFilterHasImage] = useState(null);
  const [filterHasSpecs, setFilterHasSpecs] = useState(null);
  const [filterHasTuning, setFilterHasTuning] = useState(null);
  
  // Collection Job
  const [collectionJob, setCollectionJob] = useState(null);
  const [jobPolling, setJobPolling] = useState(false);
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [collectConfig, setCollectConfig] = useState({
    brands: [],
    limit_per_brand: 20,
    year_from: 2015,
    collect_tuning: true,
    collect_image: true,
    collect_extended: true  // Enthält ALLE Daten inkl. Specs
  });
  
  // Catalog sync
  const [catalogStatus, setCatalogStatus] = useState(null);
  const [catalogSyncing, setCatalogSyncing] = useState(false);
  
  // New: Initial Build & Update
  const [initialBuildRunning, setInitialBuildRunning] = useState(false);
  const [updateRunning, setUpdateRunning] = useState(false);
  const [fullUpdateRunning, setFullUpdateRunning] = useState(false);
  const [catalogBuildStatus, setCatalogBuildStatus] = useState(null);
  
  // New: Smart Update & Add Manufacturer
  const [smartUpdateRunning, setSmartUpdateRunning] = useState(false);
  const [showAddManufacturer, setShowAddManufacturer] = useState(false);
  const [newManufacturer, setNewManufacturer] = useState({ name: '', models: '' });
  const [manufacturers, setManufacturers] = useState([]);
  
  // API Key Configuration (Workaround for deployment issues)
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKeys, setApiKeys] = useState({ openai_api_key: '', tavily_api_key: '' });
  const [apiKeyStatus, setApiKeyStatus] = useState(null);
  const [savingApiKeys, setSavingApiKeys] = useState(false);
  
  // Stop Job
  const [stoppingJob, setStoppingJob] = useState(false);
  
  // DEV/PRODUKTIV Mode & KI Validation
  const [collectorMode, setCollectorMode] = useState('dev'); // 'dev' or 'prod'
  const [loadingMode, setLoadingMode] = useState(true);
  const [validationRunning, setValidationRunning] = useState(false);
  const [validationJob, setValidationJob] = useState(null);
  
  const logContainerRef = useRef(null);
  
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // Schließe alle Modals wenn Route wechselt (Navigation-Fix)
  useEffect(() => {
    setSelectedVehicle(null);
    setShowCollectModal(false);
    setShowApiKeyModal(false);
    setShowAddManufacturer(false);
  }, [location.pathname]);

  // Load collector mode on mount
  useEffect(() => {
    loadCollectorMode();
  }, []);

  const loadCollectorMode = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/vehicle-master/collector-mode`, { headers });
      setCollectorMode(response.data.mode);
    } catch (error) {
      console.error('Failed to load collector mode:', error);
    } finally {
      setLoadingMode(false);
    }
  };

  const toggleCollectorMode = async () => {
    const newMode = collectorMode === 'dev' ? 'prod' : 'dev';
    try {
      await axios.post(`${BACKEND_URL}/api/admin/vehicle-master/collector-mode`, { mode: newMode }, { headers });
      setCollectorMode(newMode);
      toast.success(`Modus gewechselt: ${newMode === 'dev' ? 'DEV (Günstig)' : 'PRODUKTIV (Volle KI-Power)'}`);
    } catch (error) {
      toast.error('Fehler beim Modus-Wechsel: ' + (error.response?.data?.detail || error.message));
    }
  };

  const startKiValidation = async () => {
    try {
      setValidationRunning(true);
      toast.loading('Starte KI-Validierung...', { id: 'validation' });
      
      const response = await axios.post(`${BACKEND_URL}/api/admin/vehicle-master/validate-data`, {
        mode: collectorMode,
        fix_errors: true,
        delete_invalid: true
      }, { headers });
      
      setValidationJob(response.data);
      toast.success(`Validierung gestartet: ${response.data.total_to_validate} Einträge werden geprüft`, { id: 'validation' });
      
      // Poll for progress
      pollValidationJob(response.data.job_id);
    } catch (error) {
      setValidationRunning(false);
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message), { id: 'validation' });
    }
  };

  const pollValidationJob = async (jobId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/vehicle-master/collection-jobs/${jobId}`, { headers });
      setValidationJob(response.data);
      
      if (response.data.status === 'running') {
        setTimeout(() => pollValidationJob(jobId), 3000);
      } else {
        setValidationRunning(false);
        if (response.data.status === 'completed') {
          toast.success(`✅ Validierung abgeschlossen: ${response.data.fixed || 0} korrigiert, ${response.data.deleted || 0} gelöscht`, { duration: 8000 });
          loadStats(); // Refresh stats
        }
      }
    } catch (error) {
      setValidationRunning(false);
      console.error('Validation poll error:', error);
    }
  };

  // Load statistics
  const loadStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/vehicle-master/statistics`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  // Load vehicles
  const loadVehicles = async (search = '', brand = '', page = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('q', search);
      if (brand) params.append('brand', brand);
      if (filterHasImage !== null) params.append('has_image', filterHasImage);
      if (filterHasSpecs !== null) params.append('has_specs', filterHasSpecs);
      if (filterHasTuning !== null) params.append('has_tuning', filterHasTuning);
      params.append('limit', '50');
      params.append('skip', page * 50);
      
      console.log('[loadVehicles] Filter:', { filterHasImage, filterHasSpecs, filterHasTuning });
      console.log('[loadVehicles] URL:', `${BACKEND_URL}/api/admin/vehicle-master/vehicles?${params}`);
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/vehicle-master/vehicles?${params}`, { headers });
      console.log('[loadVehicles] Response:', response.data.total, 'Fahrzeuge');
      
      setVehicles(response.data.vehicles || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load vehicles:', error);
      toast.error('Fehler beim Laden der Fahrzeuge');
    } finally {
      setLoading(false);
    }
  };

  // Start collection job
  const startCollection = async () => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/vehicle-master/collect/batch`,
        collectConfig,
        { headers }
      );
      
      const jobId = response.data.job_id;
      toast.success('Sammlung gestartet!');
      setShowCollectModal(false);
      
      // Start polling
      setJobPolling(true);
      pollJobStatus(jobId);
    } catch (error) {
      toast.error(`Fehler: ${error.response?.data?.detail || error.message}`);
    }
  };
  
  // Poll job status
  const pollJobStatus = async (jobId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/vehicle-master/collection-jobs/${jobId}`,
        { headers }
      );
      
      setCollectionJob(response.data);
      
      // Scroll log to bottom
      if (logContainerRef.current) {
        logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
      }
      
      if (response.data.status === 'running') {
        setTimeout(() => pollJobStatus(jobId), 3000);
      } else {
        setJobPolling(false);
        loadStats();
        loadVehicles();
        
        if (response.data.status === 'completed') {
          toast.success('Sammlung abgeschlossen!');
        }
      }
    } catch (error) {
      console.error('Poll error:', error);
      setJobPolling(false);
    }
  };
  
  // Stop job
  const stopJob = async () => {
    if (!collectionJob?.id) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/vehicle-master/collection-jobs/${collectionJob.id}/stop`,
        {},
        { headers }
      );
      toast.info('Sammlung wird gestoppt...');
    } catch (error) {
      toast.error('Fehler beim Stoppen');
    }
  };
  
  // Migrate existing data
  const migrateData = async () => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/vehicle-master/migrate`,
        {},
        { headers }
      );
      
      toast.success(`Migration abgeschlossen: ${response.data.migrated} Einträge migriert`);
      loadStats();
      loadVehicles();
    } catch (error) {
      toast.error(`Migration fehlgeschlagen: ${error.response?.data?.detail || error.message}`);
    }
  };
  
  // Delete vehicle
  const deleteVehicle = async (vehicleId) => {
    if (!confirm('Fahrzeug wirklich löschen?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/vehicle-master/vehicles/${vehicleId}`, { headers });
      toast.success('Fahrzeug gelöscht');
      loadVehicles(searchQuery, filterBrand);
      loadStats();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };
  
  // Collect single vehicle
  const collectSingle = async (brand, model, variant) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/vehicle-master/collect`,
        { brand, model, variant, collect_tuning: true, collect_image: false, collect_extended: true },
        { headers }
      );
      if (response.data.success) {
        toast.success(`Erfolgreich gesammelt: ${response.data.collected?.join(', ')}`);
        loadVehicles();
        loadStats();
      }
    } catch (err) {
      toast.error('Fehler bei der Datensammlung');
    } finally {
      setLoading(false);
    }
  };
  
  // Load catalog sync status
  const loadCatalogStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/vehicle-master/catalog-status`, { headers });
      setCatalogStatus(response.data);
    } catch (error) {
      console.error('Failed to load catalog status:', error);
    }
  };
  
  // Load new catalog build status
  const loadCatalogBuildStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vehicle-catalog/admin/catalog-status`, { headers });
      setCatalogBuildStatus(response.data);
      
      // Only set running if actively running (don't block button forever)
      const isRunning = response.data.is_job_running && 
        response.data.recent_jobs?.some(j => 
          j.status === 'running' && 
          new Date(j.started_at) > new Date(Date.now() - 30 * 60 * 1000) // Within last 30 min
        );
      setInitialBuildRunning(isRunning);
    } catch (error) {
      console.error('Failed to load catalog build status:', error);
      setInitialBuildRunning(false); // Reset on error
    }
  };
  
  // Start initial database build (NEW)
  const startInitialBuild = async () => {
    try {
      setInitialBuildRunning(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/vehicle-catalog/admin/initial-build`,
        { collect_engines: true },
        { headers }
      );
      
      if (response.data.status === 'already_running') {
        toast.info('Ein Datenbank-Aufbau läuft bereits');
      } else {
        toast.success('Initialer Datenbank-Aufbau gestartet! Dies kann mehrere Minuten dauern.');
        
        // Start polling for status
        const pollInterval = setInterval(async () => {
          await loadCatalogBuildStatus();
          if (!catalogBuildStatus?.is_job_running) {
            clearInterval(pollInterval);
            setInitialBuildRunning(false);
            loadStats();
            loadVehicles();
          }
        }, 5000);
      }
    } catch (error) {
      toast.error(`Fehler: ${error.response?.data?.detail || error.message}`);
      setInitialBuildRunning(false);
    }
  };
  
  // Start database update (NEW)
  const startDatabaseUpdate = async () => {
    try {
      setUpdateRunning(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/vehicle-catalog/admin/update`,
        {},
        { headers }
      );
      
      toast.success('Datenbank-Update gestartet!');
      
      // Refresh after a delay
      setTimeout(() => {
        loadCatalogBuildStatus();
        loadStats();
        loadVehicles();
        setUpdateRunning(false);
      }, 10000);
    } catch (error) {
      toast.error(`Fehler: ${error.response?.data?.detail || error.message}`);
      setUpdateRunning(false);
    }
  };
  
  // Migrate legacy data to new system
  const migrateToNewSystem = async () => {
    if (!confirm('Alle Daten vom alten System ins neue System migrieren?')) return;
    
    try {
      toast.info('Migration wird gestartet...');
      const response = await axios.post(
        `${BACKEND_URL}/api/vehicle-catalog/admin/migrate-data`,
        {},
        { headers }
      );
      
      if (response.data.status === 'completed') {
        toast.success(`Migration abgeschlossen! ${response.data.migrated_vehicle_data} Fahrzeuge migriert.`);
      } else {
        toast.error(`Migration fehlgeschlagen: ${response.data.errors?.join(', ')}`);
      }
      
      loadStats();
      loadCatalogBuildStatus();
      loadVehicles();
    } catch (error) {
      toast.error(`Fehler: ${error.response?.data?.detail || error.message}`);
    }
  };
  
  // Cleanup old system after migration
  const cleanupOldSystem = async () => {
    if (!confirm('⚠️ WARNUNG: Das alte System wird unwiderruflich gelöscht! Sind Sie sicher?')) return;
    if (!confirm('Letzte Warnung: Alle alten Daten werden gelöscht. Fortfahren?')) return;
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/vehicle-catalog/admin/cleanup-legacy`,
        {},
        { headers }
      );
      
      if (response.data.status === 'completed') {
        toast.success(`Altes System entfernt. ${response.data.deleted_old_documents} Dokumente gelöscht.`);
      } else {
        toast.error(response.data.message);
      }
      
      loadStats();
      loadCatalogBuildStatus();
      loadVehicles();
    } catch (error) {
      toast.error(`Fehler: ${error.response?.data?.detail || error.message}`);
    }
  };
  
  // Start FULL database update (collects EVERYTHING)
  const startFullUpdate = async () => {
    try {
      setFullUpdateRunning(true);
      toast.info('Starte vollständiges Datenbank-Update...');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/vehicle-catalog/admin/full-update`,
        {
          collect_new_vehicles: false,
          collect_specs: true,
          collect_images: true,
          collect_tuning: true,
          collect_repair_guides: true,
          collect_torque_specs: true,
          limit_per_brand: 20,
          year_from: 2000,
          force_start: true  // Bypass running check
        },
        { headers }
      );
      
      if (response.data.status === 'already_running') {
        toast.info('Ein Update läuft bereits. Bitte warten.');
      } else {
        toast.success('Vollständiges DB Update gestartet! Dies sammelt alle Daten für die Fahrzeugdatenbank.');
        
        // Poll for completion
        const pollInterval = setInterval(async () => {
          await loadCatalogBuildStatus();
          await loadStats();
          
          if (!catalogBuildStatus?.is_job_running) {
            clearInterval(pollInterval);
            setFullUpdateRunning(false);
            loadVehicles();
            toast.success('Vollständiges Update abgeschlossen!');
          }
        }, 5000);
        
        // Safety timeout after 30 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          setFullUpdateRunning(false);
        }, 30 * 60 * 1000);
      }
    } catch (error) {
      toast.error(`Fehler: ${error.response?.data?.detail || error.message}`);
      setFullUpdateRunning(false);
    }
  };

  // Stop running job
  const stopCurrentJob = async () => {
    const jobId = catalogBuildStatus?.recent_jobs?.[0]?.id;
    if (!jobId) {
      toast.error('Kein aktiver Job zum Stoppen gefunden');
      return;
    }
    
    try {
      setStoppingJob(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/vehicle-catalog/admin/stop-job/${jobId}`,
        {},
        { headers }
      );
      
      if (response.data.status === 'ok') {
        toast.success('Job wird gestoppt...');
        setInitialBuildRunning(false);
        setFullUpdateRunning(false);
        // Refresh status
        setTimeout(() => loadCatalogBuildStatus(), 1000);
      }
    } catch (error) {
      toast.error(`Fehler beim Stoppen: ${error.response?.data?.detail || error.message}`);
    } finally {
      setStoppingJob(false);
    }
  };

  // Test and check API keys
  const testApiKeys = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vehicle-catalog/admin/test-openai`, { headers });
      setApiKeyStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to test API keys:', error);
      return null;
    }
  };
  
  // Save API keys to database
  const saveApiKeys = async () => {
    if (!apiKeys.openai_api_key && !apiKeys.tavily_api_key) {
      toast.error('Bitte mindestens einen API-Key eingeben');
      return;
    }
    
    try {
      setSavingApiKeys(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/vehicle-catalog/admin/save-api-keys`,
        apiKeys,
        { headers }
      );
      
      if (response.data.status === 'success') {
        toast.success('API-Keys erfolgreich gespeichert!');
        setShowApiKeyModal(false);
        // Test the keys after saving
        await testApiKeys();
      } else {
        toast.error(`Fehler: ${response.data.error}`);
      }
    } catch (error) {
      toast.error(`Fehler beim Speichern: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSavingApiKeys(false);
    }
  };

  // VEREINFACHTER UPDATE - Prüft und sammelt ALLES was fehlt
  const startSmartUpdate = async () => {
    try {
      setSmartUpdateRunning(true);
      toast.info('🔄 Starte Update - prüfe fehlende Daten...');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/vehicle-catalog/admin/smart-update`,
        {
          // Sammle alles was fehlt
          collect_specs: true,
          collect_tuning: true,
          collect_hardware: true,
          collect_repair_guides: true,
          collect_images: true,
          collect_advanced_data: true,
          // Validierung aktivieren
          validate_data: true,
          // Nur fehlende Felder sammeln
          skip_complete_vehicles: true,
          // ECU-spezifische Hardware-Recherche
          use_llm_for_hardware: true,
          force_start: true
        },
        { headers }
      );
      
      if (response.data.status === 'already_running') {
        toast.info('Ein Update läuft bereits. Bitte warten.');
        setSmartUpdateRunning(false);
      } else {
        toast.success('🔄 Update gestartet! Sammelt nur fehlende Daten.');
        
        // Poll for completion
        const pollInterval = setInterval(async () => {
          await loadCatalogBuildStatus();
          await loadStats();
          
          if (!catalogBuildStatus?.is_job_running) {
            clearInterval(pollInterval);
            setSmartUpdateRunning(false);
            loadVehicles();
            toast.success('✅ Update abgeschlossen!');
          }
        }, 5000);
        
        // Safety timeout
        setTimeout(() => {
          clearInterval(pollInterval);
          setSmartUpdateRunning(false);
        }, 60 * 60 * 1000); // 1 Stunde
      }
    } catch (error) {
      toast.error(`Fehler: ${error.response?.data?.detail || error.message}`);
      setSmartUpdateRunning(false);
    }
  };

  // NEU: Hersteller hinzufügen
  const addManufacturer = async () => {
    if (!newManufacturer.name.trim()) {
      toast.error('Bitte Herstellername eingeben');
      return;
    }
    
    try {
      const models = newManufacturer.models
        ? newManufacturer.models.split(',').map(m => m.trim()).filter(m => m)
        : [];
      
      const response = await axios.post(
        `${BACKEND_URL}/api/vehicle-catalog/admin/add-manufacturer`,
        {
          name: newManufacturer.name.trim(),
          models: models
        },
        { headers }
      );
      
      if (response.data.success) {
        toast.success(`✅ Hersteller "${newManufacturer.name}" hinzugefügt!`);
        setShowAddManufacturer(false);
        setNewManufacturer({ name: '', models: '' });
        loadCatalogBuildStatus();
        loadStats();
      } else {
        toast.error(response.data.error || 'Fehler beim Hinzufügen');
      }
    } catch (error) {
      toast.error(`Fehler: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Load manufacturers list
  const loadManufacturers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vehicle-catalog/admin/manufacturers`, { headers });
      setManufacturers(response.data || []);
    } catch (error) {
      console.error('Failed to load manufacturers:', error);
    }
  };

  useEffect(() => {
    loadStats();
    loadVehicles();
    loadCatalogStatus();
    loadCatalogBuildStatus();
    testApiKeys(); // Check API keys on load
  }, []);
  
  // Auto-refresh while job is running
  useEffect(() => {
    let interval;
    if (catalogBuildStatus?.is_job_running) {
      interval = setInterval(() => {
        loadCatalogBuildStatus();
      }, 5000); // Refresh every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [catalogBuildStatus?.is_job_running]);
  
  useEffect(() => {
    loadVehicles(searchQuery, filterBrand);
  }, [filterHasImage, filterHasSpecs, filterHasTuning]);

  // Debounced live search - reload when searchQuery changes (after typing pause)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchMode === 'text') {
        loadVehicles(searchQuery, filterBrand);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset all filters & search
  const resetFilters = () => {
    setSearchQuery('');
    setFilterBrand('');
    setFilterHasImage(null);
    setFilterHasSpecs(null);
    setFilterHasTuning(null);
    setDropdownSelection(null);
    // Reload without filters
    setTimeout(() => loadVehicles('', ''), 50);
  };

  const hasActiveFilters = !!(searchQuery || filterBrand || filterHasImage !== null || filterHasSpecs !== null || filterHasTuning !== null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0F16] to-[#111827] p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Database className="w-8 h-8 text-cyan-400" />
              Fahrzeug-Datenbank
            </h1>
            <p className="text-slate-400 mt-1">
              Zentrales Datensystem für alle Fahrzeuginformationen
            </p>
          </div>
          
          <div className="flex gap-2 flex-wrap items-center">
            {/* DEV/PRODUKTIV Mode Switch */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700">
              <span className={`text-xs font-medium ${collectorMode === 'dev' ? 'text-green-400' : 'text-slate-500'}`}>
                DEV
              </span>
              <button
                onClick={toggleCollectorMode}
                disabled={loadingMode}
                className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                  collectorMode === 'prod' 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                    : 'bg-gradient-to-r from-green-500 to-emerald-500'
                }`}
                title={collectorMode === 'dev' 
                  ? 'DEV: Nur OpenAI (günstig ~$0.03/Fahrzeug)' 
                  : 'PRODUKTIV: Gemini + GPT + Claude (genau ~$0.15/Fahrzeug)'}
                data-testid="mode-toggle"
              >
                <span 
                  className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-lg transition-transform duration-300 ${
                    collectorMode === 'prod' ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-xs font-medium ${collectorMode === 'prod' ? 'text-purple-400' : 'text-slate-500'}`}>
                PROD
              </span>
            </div>
            
            {/* KI Validierung Button */}
            <Button
              onClick={startKiValidation}
              disabled={validationRunning || catalogBuildStatus?.is_job_running}
              className={`${
                collectorMode === 'prod'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                  : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
              }`}
              title={`KI-Validierung (${collectorMode === 'dev' ? 'Günstig' : 'Volle Power'}): Prüft alle Daten auf Richtigkeit, korrigiert Fehler, entfernt Fake-Daten`}
              data-testid="ki-validation-btn"
            >
              {validationRunning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Validiere...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  🔍 KI Validierung
                </>
              )}
            </Button>
            
            {/* Validation Progress Indicator */}
            {validationJob && validationRunning && (
              <div className="px-3 py-1 rounded-lg bg-slate-800/80 border border-cyan-500/30">
                <div className="text-xs text-cyan-400">
                  {validationJob.processed || 0}/{validationJob.total_to_validate || '?'} geprüft
                </div>
                {(validationJob.fixed > 0 || validationJob.deleted > 0) && (
                  <div className="text-xs text-green-400">
                    ✓ {validationJob.fixed || 0} korr. | 🗑 {validationJob.deleted || 0} gel.
                  </div>
                )}
              </div>
            )}
            
            {/* STOP BUTTON - nur sichtbar wenn Job läuft */}
            {catalogBuildStatus?.is_job_running && (
              <Button
                onClick={stopCurrentJob}
                disabled={stoppingJob}
                className="bg-red-600 hover:bg-red-700 text-white animate-pulse"
                title="Aktuellen Job stoppen"
              >
                {stoppingJob ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                ⏹️ STOP
              </Button>
            )}
            
            {/* Hersteller hinzufügen */}
            <Button
              onClick={() => { loadManufacturers(); setShowAddManufacturer(true); }}
              variant="outline"
              className="border-green-400/50 text-green-400 hover:bg-green-400/10"
              title="Neuen Hersteller zur Datenbank hinzufügen"
            >
              <Database className="w-4 h-4 mr-2" />
              ➕ Hersteller
            </Button>
            
            {/* EIN Update-Button - prüft und sammelt alles was fehlt */}
            <Button
              onClick={startSmartUpdate}
              disabled={smartUpdateRunning || catalogBuildStatus?.is_job_running}
              className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700"
              title="Prüft alle Fahrzeuge, sammelt fehlende Daten (Specs, Tuning, Hardware, Anleitungen)"
            >
              {smartUpdateRunning ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              🔄 Update
            </Button>
            
            <Button
              onClick={() => { testApiKeys(); setShowApiKeyModal(true); }}
              variant="outline"
              className={`border-yellow-400/50 text-yellow-400 hover:bg-yellow-400/10 ${
                apiKeyStatus?.status === 'failed' ? 'animate-pulse border-red-500' : ''
              }`}
              title="API-Keys konfigurieren (für Datensammlung)"
            >
              <Settings className="w-4 h-4 mr-2" />
              🔑 API-Keys
            </Button>
          </div>
        </div>
        
        {/* API Key Configuration Modal */}
        {showApiKeyModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <Card className="bg-[#1A2332] border-yellow-400/30 w-full max-w-lg mx-4">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Settings className="w-5 h-5 text-yellow-400" />
                  API-Keys konfigurieren
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Status */}
                {apiKeyStatus && (
                  <div className={`p-3 rounded-lg ${
                    apiKeyStatus.status === 'success' 
                      ? 'bg-emerald-500/20 border border-emerald-500/50' 
                      : 'bg-red-500/20 border border-red-500/50'
                  }`}>
                    <p className="text-sm text-white font-medium">
                      {apiKeyStatus.status === 'success' ? '✅ API-Verbindung funktioniert!' : '❌ API-Verbindung fehlgeschlagen'}
                    </p>
                    {apiKeyStatus.openai_key_preview && (
                      <p className="text-xs text-slate-300 mt-1">
                        Aktueller Key: {apiKeyStatus.openai_key_preview}
                        {apiKeyStatus.openai_key_from_db && ' (aus Datenbank)'}
                      </p>
                    )}
                    {apiKeyStatus.error && (
                      <p className="text-xs text-red-300 mt-1">{apiKeyStatus.error}</p>
                    )}
                  </div>
                )}
                
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <p className="text-amber-200 text-sm">
                    ⚠️ <strong>Wichtig:</strong> Speichern Sie hier Ihre API-Keys, damit sie in der Datenbank 
                    gespeichert werden. Dies ist ein Workaround für Deployment-Probleme.
                  </p>
                </div>
                
                <div>
                  <label className="text-slate-300 text-sm block mb-1">OpenAI API Key</label>
                  <Input
                    type="password"
                    placeholder="sk-proj-..."
                    value={apiKeys.openai_api_key}
                    onChange={(e) => setApiKeys({...apiKeys, openai_api_key: e.target.value})}
                    className="bg-[#0D1117] border-slate-600 text-white"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Holen Sie sich den Key von <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">platform.openai.com</a>
                  </p>
                </div>
                
                <div>
                  <label className="text-slate-300 text-sm block mb-1">Tavily API Key (optional)</label>
                  <Input
                    type="password"
                    placeholder="tvly-..."
                    value={apiKeys.tavily_api_key}
                    onChange={(e) => setApiKeys({...apiKeys, tavily_api_key: e.target.value})}
                    className="bg-[#0D1117] border-slate-600 text-white"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Für Web-Suche: <a href="https://tavily.com" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">tavily.com</a>
                  </p>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={saveApiKeys}
                    disabled={savingApiKeys}
                    className="flex-1 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700"
                  >
                    {savingApiKeys ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Speichern & Testen
                  </Button>
                  <Button
                    onClick={() => setShowApiKeyModal(false)}
                    variant="outline"
                    className="border-slate-600 text-slate-300"
                  >
                    Schließen
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* NEU: Hersteller hinzufügen Modal */}
        {showAddManufacturer && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <Card className="bg-[#1A2332] border-green-400/30 w-full max-w-lg mx-4">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Database className="w-5 h-5 text-green-400" />
                  Hersteller hinzufügen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                  <p className="text-green-200 text-sm">
                    Fügen Sie einen neuen Fahrzeughersteller zur Datenbank hinzu. 
                    Nach dem Hinzufügen können Sie mit &quot;Intelligent Update&quot; die Daten sammeln.
                  </p>
                </div>
                
                <div>
                  <label className="text-slate-300 text-sm block mb-1">Herstellername *</label>
                  <Input
                    placeholder="z.B. Tesla, Lucid, Rivian..."
                    value={newManufacturer.name}
                    onChange={(e) => setNewManufacturer({...newManufacturer, name: e.target.value})}
                    className="bg-[#0D1117] border-slate-600 text-white"
                  />
                </div>
                
                <div>
                  <label className="text-slate-300 text-sm block mb-1">Modelle (optional, kommagetrennt)</label>
                  <Input
                    placeholder="z.B. Model S, Model 3, Model X, Model Y"
                    value={newManufacturer.models}
                    onChange={(e) => setNewManufacturer({...newManufacturer, models: e.target.value})}
                    className="bg-[#0D1117] border-slate-600 text-white"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Modelle können auch später durch das Update gesammelt werden.
                  </p>
                </div>
                
                {/* Existierende Hersteller */}
                {manufacturers.length > 0 && (
                  <div className="bg-slate-800/50 rounded-lg p-3 max-h-40 overflow-y-auto">
                    <p className="text-xs text-slate-400 mb-2">Existierende Hersteller ({manufacturers.length}):</p>
                    <div className="flex flex-wrap gap-1">
                      {manufacturers.map((m, i) => (
                        <span key={i} className="text-xs bg-slate-700 px-2 py-1 rounded text-slate-300">
                          {m.name} ({m.models_count})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={addManufacturer}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Hinzufügen
                  </Button>
                  <Button
                    onClick={() => setShowAddManufacturer(false)}
                    variant="outline"
                    className="border-slate-600 text-slate-300"
                  >
                    Abbrechen
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* VOLLSTÄNDIGE FAHRZEUGE - Was wirklich komplett ist */}
        {stats && (
          <>
            <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-emerald-400/30">
              <CardContent className="p-4">
                <h3 className="text-white font-medium flex items-center gap-2 mb-4">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Vollständige Fahrzeuge (mit allen technischen Daten)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <StatCard title="Vollständig" value={stats.total_vehicles} icon={Car} color="emerald" />
                  <StatCard title="Mit Bild" value={stats.with_image} icon={Image} color="green" />
                  <StatCard title="Mit Specs" value={stats.with_specs} icon={Settings} color="blue" />
                  <StatCard title="Mit Tuning" value={stats.with_tuning} icon={Zap} color="amber" />
                  <StatCard title="Drehmomente" value={stats.with_torque || 0} icon={Wrench} color="orange" />
                  <StatCard title="Reparaturen" value={stats.with_repairs || 0} icon={Tool} color="red" />
                </div>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2">
                  <MiniStatCard title="Klimaanlage" value={stats.with_ac || 0} icon={Thermometer} />
                  <MiniStatCard title="Positionen" value={stats.with_locations || 0} icon={MapPin} />
                  <MiniStatCard title="Sicherungen" value={stats.with_fuses || 0} icon={Shield} />
                  <MiniStatCard title="Steuergeräte" value={stats.with_ecus || 0} icon={Cpu} />
                  <MiniStatCard title="Batterie" value={stats.with_battery || 0} icon={Battery} />
                </div>
                
                {/* Button für fehlende Bilder generieren */}
                {stats.total_vehicles - stats.with_image > 0 && (
                  <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Image className="w-5 h-5 text-amber-400" />
                        <div>
                          <span className="text-amber-400 font-medium">
                            {stats.total_vehicles - stats.with_image} Fahrzeuge ohne Bild
                          </span>
                          <p className="text-xs text-gray-400 mt-0.5">
                            KI-generierte Studiofotos für professionelle Darstellung
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            toast.loading('Starte Bildgenerierung...', { id: 'gen-images' });
                            const response = await axios.post(`${BACKEND_URL}/api/admin/vehicle-master/generate-missing-images`, {
                              limit: 10
                            });
                            toast.success(`${response.data.message}. Job ID: ${response.data.job_id}`, { id: 'gen-images' });
                          } catch (error) {
                            toast.error('Fehler: ' + (error.response?.data?.detail || error.message), { id: 'gen-images' });
                          }
                        }}
                        className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                        data-testid="generate-missing-images-btn"
                      >
                        <Image className="w-4 h-4 mr-2" />
                        Bilder generieren
                      </Button>
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 mt-3">
                  Diese {stats.total_vehicles} Fahrzeuge haben alle technischen Daten inkl. Bilder, Spezifikationen, Tuning-Daten, Drehmomente und Reparaturanleitungen.
                </p>
              </CardContent>
            </Card>
            
            {/* Fahrzeugkatalog - Struktur (ohne technische Daten) */}
            {catalogBuildStatus && (
              <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-amber-400/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-medium flex items-center gap-2">
                      <Database className="w-4 h-4 text-amber-400" />
                      Fahrzeugkatalog (nur Struktur - noch ohne technische Daten)
                    </h3>
                    {catalogBuildStatus.is_job_running && (
                      <span className="flex items-center gap-1 text-xs text-amber-400">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Sammlung läuft...
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4 mb-3">
                    <div className="text-center p-2 bg-white/5 rounded-lg border border-amber-400/20">
                      <p className="text-xl font-bold text-amber-400">{catalogBuildStatus.manufacturers_count || catalogBuildStatus.new_catalog?.manufacturers_count || 0}</p>
                      <p className="text-xs text-gray-400">Hersteller</p>
                    </div>
                    <div className="text-center p-2 bg-white/5 rounded-lg border border-amber-400/20">
                      <p className="text-xl font-bold text-amber-300">{catalogBuildStatus.total_models || catalogBuildStatus.new_catalog?.total_models || 0}</p>
                      <p className="text-xs text-gray-400">Modelle</p>
                    </div>
                    <div className="text-center p-2 bg-white/5 rounded-lg border border-amber-400/20">
                      <p className="text-xl font-bold text-amber-200">{catalogBuildStatus.total_generations || catalogBuildStatus.new_catalog?.total_generations || 0}</p>
                      <p className="text-xs text-gray-400">Generationen</p>
                    </div>
                    <div className="text-center p-2 bg-white/5 rounded-lg border border-amber-400/20">
                      <p className="text-xl font-bold text-amber-100">{catalogBuildStatus.total_engine_documents || catalogBuildStatus.new_catalog?.engine_documents || 0}</p>
                      <p className="text-xs text-gray-400">Motor-Varianten</p>
                    </div>
                  </div>
                  
                  {/* Hinweis: Was fehlt */}
                  {(catalogBuildStatus.manufacturers_count || catalogBuildStatus.new_catalog?.manufacturers_count) > 0 && stats && (
                    <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mb-3">
                      <p className="text-sm text-blue-400">
                        💡 Diese {catalogBuildStatus.total_models || catalogBuildStatus.new_catalog?.total_models || 0} Modelle sind im Katalog, aber nur {stats.total_vehicles} haben vollständige technische Daten.
                        <br />
                        <strong>Klicken Sie auf &quot;🚀 Vollständiges DB Update&quot;</strong> um Bilder, Specs, Tuning, Drehmomente und Reparaturanleitungen zu sammeln.
                      </p>
                    </div>
                  )}
                  
                  {(catalogBuildStatus.manufacturers_count || catalogBuildStatus.new_catalog?.manufacturers_count || 0) === 0 && !catalogBuildStatus.is_job_running && (
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <p className="text-sm text-amber-400">
                        ⚠️ Der Fahrzeugkatalog ist leer. Klicken Sie auf &quot;Struktur aufbauen&quot; um Hersteller/Modelle/Generationen zu sammeln.
                      </p>
                    </div>
                  )}
                  
                  {/* LIVE Progress Display */}
                  {catalogBuildStatus.is_job_running && catalogBuildStatus.recent_jobs?.[0] && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-400/30 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-purple-300 flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Datenbank-Aufbau läuft
                        </span>
                        <span className="text-lg font-bold text-white">
                          {Math.round((catalogBuildStatus.recent_jobs[0]?.manufacturers_processed || 0) / (catalogBuildStatus.recent_jobs[0]?.manufacturers_total || 41) * 100)}%
                        </span>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="w-full h-3 bg-black/30 rounded-full overflow-hidden mb-3">
                        <div 
                          className="h-full bg-gradient-to-r from-purple-500 to-cyan-400 transition-all duration-1000 ease-out"
                          style={{ 
                            width: `${Math.round((catalogBuildStatus.recent_jobs[0]?.manufacturers_processed || 0) / (catalogBuildStatus.recent_jobs[0]?.manufacturers_total || 41) * 100)}%` 
                          }}
                        />
                      </div>
                      
                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                        <div className="bg-black/20 rounded-lg p-2">
                          <p className="text-lg font-bold text-purple-400">
                            {catalogBuildStatus.recent_jobs[0]?.manufacturers_processed || 0}/{catalogBuildStatus.recent_jobs[0]?.manufacturers_total || 41}
                          </p>
                          <p className="text-xs text-gray-500">Hersteller</p>
                        </div>
                        <div className="bg-black/20 rounded-lg p-2">
                          <p className="text-lg font-bold text-cyan-400">
                            {catalogBuildStatus.recent_jobs[0]?.models_collected || 0}
                          </p>
                          <p className="text-xs text-gray-500">Modelle</p>
                        </div>
                        <div className="bg-black/20 rounded-lg p-2">
                          <p className="text-lg font-bold text-green-400">
                            {catalogBuildStatus.recent_jobs[0]?.generations_collected || 0}
                          </p>
                          <p className="text-xs text-gray-500">Generationen</p>
                        </div>
                      </div>
                      
                      {/* Live Log */}
                      {catalogBuildStatus.recent_jobs[0]?.log?.length > 0 && (
                        <div className="bg-black/30 rounded-lg p-3 max-h-32 overflow-y-auto">
                          <p className="text-xs text-gray-500 mb-1">Live-Aktivität:</p>
                          {catalogBuildStatus.recent_jobs[0].log.slice(-5).map((log, idx) => (
                            <p key={idx} className="text-xs text-gray-300 font-mono">
                              {log}
                            </p>
                          ))}
                        </div>
                      )}
                      
                      {/* Errors */}
                      {catalogBuildStatus.recent_jobs[0]?.errors?.length > 0 && (
                        <div className="mt-2 text-xs text-red-400">
                          ⚠️ {catalogBuildStatus.recent_jobs[0].errors.length} Fehler aufgetreten
                        </div>
                      )}
                      
                      {/* STOP BUTTON im Job-Status-Bereich */}
                      <div className="mt-4 pt-3 border-t border-white/10">
                        <Button
                          onClick={stopCurrentJob}
                          disabled={stoppingJob}
                          className="w-full bg-red-600 hover:bg-red-700 text-white"
                        >
                          {stoppingJob ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <StopCircle className="w-4 h-4 mr-2" />
                          )}
                          Job stoppen
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Completed Job Info */}
                  {!catalogBuildStatus.is_job_running && catalogBuildStatus.recent_jobs?.[0]?.status === 'completed' && (
                    <div className="mt-3 p-3 bg-green-500/10 border border-green-400/30 rounded-lg">
                      <p className="text-sm text-green-400 flex items-center gap-2">
                        ✅ Datenbank-Aufbau abgeschlossen am {new Date(catalogBuildStatus.recent_jobs[0]?.completed_at).toLocaleString('de-DE')}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Old Catalog Sync Status */}
            {catalogStatus && (
              <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-medium flex items-center gap-2">
                      <Database className="w-4 h-4 text-cyan-400" />
                      Master-Daten Synchronisation
                    </h3>
                    <span className="text-xs text-gray-400">
                      {catalogStatus.synced}/{catalogStatus.catalog_total} synchronisiert ({catalogStatus.percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2 mb-3">
                    <div 
                      className="h-2 rounded-full bg-gradient-to-r from-cyan-500 to-green-500 transition-all duration-500"
                      style={{ width: `${catalogStatus.percentage}%` }}
                    />
                  </div>
                  {catalogStatus.missing_count > 0 && (
                    <p className="text-sm text-amber-400">
                      {catalogStatus.missing_count} Fahrzeuge aus dem Katalog fehlen noch
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
        
        {/* Active Collection Job */}
        {collectionJob && collectionJob.status === 'running' && (
          <Card className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border-cyan-400/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
                  Sammlung läuft...
                </div>
                <Button onClick={stopJob} variant="destructive" size="sm">
                  <Square className="w-4 h-4 mr-2" />
                  Stoppen
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-cyan-400">{collectionJob.progress?.processed_brands || 0}/{collectionJob.progress?.total_brands || 0}</p>
                  <p className="text-xs text-gray-400">Marken</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-400">{collectionJob.progress?.successful || 0}</p>
                  <p className="text-xs text-gray-400">Erfolgreich</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-400">{collectionJob.progress?.failed || 0}</p>
                  <p className="text-xs text-gray-400">Fehlgeschlagen</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-white truncate">{collectionJob.progress?.current_brand || '-'}</p>
                  <p className="text-xs text-gray-400">Aktuelle Marke</p>
                </div>
              </div>
              
              {/* Log Output */}
              <div 
                ref={logContainerRef}
                className="bg-black/50 rounded-lg p-3 max-h-48 overflow-y-auto font-mono text-xs"
              >
                {(collectionJob.log || []).slice(-20).map((entry, idx) => (
                  <div key={idx} className={`${entry.message.includes('✓') ? 'text-green-400' : entry.message.includes('✗') ? 'text-red-400' : 'text-gray-300'}`}>
                    <span className="text-gray-500">{new Date(entry.timestamp).toLocaleTimeString()}</span>{' '}
                    {entry.message}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vehicle Search with Cascading Dropdown */}
        <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Car className="w-5 h-5 text-cyan-400" />
                Fahrzeug suchen
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant={searchMode === 'dropdown' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSearchMode('dropdown')}
                  className={searchMode === 'dropdown' ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-400'}
                >
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Auswahl
                </Button>
                <Button
                  variant={searchMode === 'text' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSearchMode('text')}
                  className={searchMode === 'text' ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-400'}
                >
                  <Search className="w-4 h-4 mr-1" />
                  Textsuche
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {searchMode === 'dropdown' ? (
              <div className="space-y-4">
                <VehicleCascadingDropdown
                  onSelectionComplete={(selection) => {
                    setDropdownSelection(selection);
                    if (selection.manufacturer && selection.model) {
                      // Automatisch nach Auswahl suchen
                      setFilterBrand(selection.manufacturer);
                      setSearchQuery(selection.model);
                    }
                  }}
                  showEngines={true}
                  compact={true}
                />
                
                {dropdownSelection?.isComplete && (
                  <Button 
                    onClick={() => loadVehicles(dropdownSelection.model, dropdownSelection.manufacturer)} 
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Fahrzeug suchen: {dropdownSelection.displayName}
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Suche nach Marke, Modell..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && loadVehicles(searchQuery, filterBrand)}
                      className="pl-10 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
                
                <select
                  value={filterBrand}
                  onChange={(e) => {
                    setFilterBrand(e.target.value);
                    loadVehicles(searchQuery, e.target.value);
                  }}
                  className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white"
                >
                  <option value="">Alle Marken</option>
                  {stats?.brands?.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
                
                <Button 
                  onClick={() => loadVehicles(searchQuery, filterBrand)} 
                  variant="outline" 
                  className="border-cyan-400/50 text-cyan-400"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Suchen
                </Button>
              </div>
            )}
            
            {/* Filter Row */}
            <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-white/10">
              <span className="text-xs text-gray-400 mr-2">Filter:</span>
              <FilterButton 
                label="Bild" 
                active={filterHasImage} 
                onClick={() => setFilterHasImage(filterHasImage === true ? null : true)}
                onClickNo={() => setFilterHasImage(filterHasImage === false ? null : false)}
              />
              <FilterButton 
                label="Specs" 
                active={filterHasSpecs} 
                onClick={() => setFilterHasSpecs(filterHasSpecs === true ? null : true)}
                onClickNo={() => setFilterHasSpecs(filterHasSpecs === false ? null : false)}
              />
              <FilterButton 
                label="Tuning" 
                active={filterHasTuning} 
                onClick={() => setFilterHasTuning(filterHasTuning === true ? null : true)}
                onClickNo={() => setFilterHasTuning(filterHasTuning === false ? null : false)}
              />

              {hasActiveFilters && (
                <Button
                  onClick={resetFilters}
                  variant="ghost"
                  size="sm"
                  className="ml-auto text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  data-testid="reset-filters-btn"
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" />
                  Filter zurücksetzen
                </Button>
              )}
            </div>

            {/* Active Filter Chips */}
            {hasActiveFilters && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="active-filter-chips">
                {searchQuery && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 text-xs">
                    Suche: &quot;{searchQuery}&quot;
                    <button onClick={() => setSearchQuery('')} className="hover:text-white">×</button>
                  </span>
                )}
                {filterBrand && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 text-xs">
                    Marke: {filterBrand}
                    <button onClick={() => { setFilterBrand(''); loadVehicles(searchQuery, ''); }} className="hover:text-white">×</button>
                  </span>
                )}
                {filterHasImage !== null && (
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${filterHasImage ? 'bg-green-500/15 border border-green-500/40 text-green-300' : 'bg-red-500/15 border border-red-500/40 text-red-300'}`}>
                    {filterHasImage ? 'Mit Bild' : 'Ohne Bild'}
                    <button onClick={() => setFilterHasImage(null)} className="hover:text-white">×</button>
                  </span>
                )}
                {filterHasSpecs !== null && (
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${filterHasSpecs ? 'bg-green-500/15 border border-green-500/40 text-green-300' : 'bg-red-500/15 border border-red-500/40 text-red-300'}`}>
                    {filterHasSpecs ? 'Mit Specs' : 'Ohne Specs'}
                    <button onClick={() => setFilterHasSpecs(null)} className="hover:text-white">×</button>
                  </span>
                )}
                {filterHasTuning !== null && (
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${filterHasTuning ? 'bg-green-500/15 border border-green-500/40 text-green-300' : 'bg-red-500/15 border border-red-500/40 text-red-300'}`}>
                    {filterHasTuning ? 'Mit Tuning' : 'Ohne Tuning'}
                    <button onClick={() => setFilterHasTuning(null)} className="hover:text-white">×</button>
                  </span>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Vehicle List */}
        <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <span>Fahrzeuge ({totalCount})</span>
              <Button onClick={() => loadVehicles(searchQuery, filterBrand)} variant="ghost" size="sm" className="text-gray-400">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
              </div>
            ) : vehicles.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Car className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Keine Fahrzeuge gefunden</p>
                <Button onClick={migrateData} variant="link" className="text-cyan-400 mt-2">
                  Bestehende Daten migrieren
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-gray-400 text-sm">
                      <th className="pb-3">Fahrzeug</th>
                      <th className="pb-3">Baujahr</th>
                      <th className="pb-3 text-center">Bild</th>
                      <th className="pb-3 text-center">Specs</th>
                      <th className="pb-3 text-center">Tuning</th>
                      <th className="pb-3 text-center">Vollständig</th>
                      <th className="pb-3">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody className="text-white">
                    {vehicles.map((vehicle) => (
                      <tr key={vehicle.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-3">
                          <div className="flex items-center gap-3">
                            {vehicle.image?.path ? (
                              <img 
                                src={`${BACKEND_URL}/api${vehicle.image.path}`} 
                                alt="" 
                                className="w-12 h-12 rounded-lg object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className={`w-12 h-12 rounded-lg bg-gray-700 items-center justify-center ${vehicle.image?.path ? 'hidden' : 'flex'}`}>
                                <Car className="w-6 h-6 text-gray-500" />
                              </div>
                            <div>
                              <p className="font-medium">{vehicle.brand} {vehicle.model}</p>
                              {vehicle.variant && (
                                <p className="text-xs text-gray-400">{vehicle.variant}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 text-gray-400">
                          {vehicle.year_start && vehicle.year_end 
                            ? `${vehicle.year_start}-${vehicle.year_end}`
                            : vehicle.year_start || '-'}
                        </td>
                        <td className="py-3 text-center">
                          <StatusBadge has={!!vehicle.image?.path} />
                        </td>
                        <td className="py-3 text-center">
                          <StatusBadge has={!!vehicle.specifications} />
                        </td>
                        <td className="py-3 text-center">
                          <StatusBadge has={!!vehicle.tuning} />
                        </td>
                        <td className="py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <div className="w-16 bg-gray-700 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500"
                                style={{ width: `${vehicle.completeness || 0}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-400">{vehicle.completeness || 0}%</span>
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-cyan-400 h-8 w-8 p-0"
                              onClick={() => collectSingle(vehicle.brand, vehicle.model, vehicle.variant)}
                              title="Daten sammeln"
                            >
                              <Zap className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-gray-400 h-8 w-8 p-0"
                              onClick={() => setSelectedVehicle(vehicle)}
                              title="Details anzeigen"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-400 h-8 w-8 p-0"
                              onClick={() => deleteVehicle(vehicle.id)}
                              title="Löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Collection Modal */}
        {showCollectModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-cyan-400/30 w-full max-w-lg mx-4">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-cyan-400" />
                  KI-Datensammlung starten
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-400 text-sm">
                  Die KI sammelt automatisch Spezifikationen, Tuning-Daten und Bilder für alle Fahrzeuge im Katalog.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-white text-sm mb-1 block">Baujahr ab</label>
                    <Input
                      type="number"
                      value={collectConfig.year_from}
                      onChange={(e) => setCollectConfig({...collectConfig, year_from: parseInt(e.target.value)})}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-white text-sm mb-1 block">Max. pro Marke</label>
                    <Input
                      type="number"
                      value={collectConfig.limit_per_brand}
                      onChange={(e) => setCollectConfig({...collectConfig, limit_per_brand: parseInt(e.target.value)})}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-white">
                    <input 
                      type="checkbox" 
                      checked={collectConfig.collect_specs}
                      onChange={(e) => setCollectConfig({...collectConfig, collect_specs: e.target.checked})}
                      className="rounded"
                    />
                    Technische Spezifikationen sammeln
                  </label>
                  <label className="flex items-center gap-2 text-white">
                    <input 
                      type="checkbox" 
                      checked={collectConfig.collect_tuning}
                      onChange={(e) => setCollectConfig({...collectConfig, collect_tuning: e.target.checked})}
                      className="rounded"
                    />
                    Tuning-Daten sammeln
                  </label>
                  <label className="flex items-center gap-2 text-white">
                    <input 
                      type="checkbox" 
                      checked={collectConfig.collect_image}
                      onChange={(e) => setCollectConfig({...collectConfig, collect_image: e.target.checked})}
                      className="rounded"
                    />
                    Bilder generieren
                  </label>
                </div>
                
                <div className="bg-amber-900/30 border border-amber-400/30 rounded-lg p-3">
                  <p className="text-amber-200 text-sm">
                    <strong>Hinweis:</strong> Die Sammlung kann je nach Anzahl der Fahrzeuge mehrere Stunden dauern. 
                    Der Fortschritt wird live angezeigt.
                  </p>
                </div>
                
                <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowCollectModal(false)}
                    className="border-white/20 text-white"
                  >
                    Abbrechen
                  </Button>
                  <Button 
                    onClick={startCollection}
                    className="bg-gradient-to-r from-cyan-500 to-blue-600"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Sammlung starten
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Vehicle Details Modal - Enhanced View */}
        {selectedVehicle && (
          <VehicleDetailView 
            vehicle={selectedVehicle}
            onClose={() => setSelectedVehicle(null)}
            onSave={async (updatedData) => {
              try {
                const token = localStorage.getItem('token');
                await axios.put(
                  `${BACKEND_URL}/api/admin/vehicle-master/vehicles/${updatedData.id}`,
                  updatedData,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Fahrzeugdaten gespeichert');
                loadVehicles();
              } catch (err) {
                toast.error('Fehler beim Speichern');
              }
            }}
            isAdmin={true}
          />
        )}
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ title, value, icon: Icon, color }) => (
  <Card className={`bg-gradient-to-br from-${color}-900/20 to-transparent border-${color}-500/20`}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`p-2 rounded-lg bg-${color}-500/20`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-xs text-gray-400">{title}</p>
      </div>
    </CardContent>
  </Card>
);

// Smaller stat card for extended data
const MiniStatCard = ({ title, value, icon: Icon }) => (
  <div className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center gap-2">
    <Icon className="w-4 h-4 text-cyan-400" />
    <div>
      <p className="text-lg font-bold text-white">{value}</p>
      <p className="text-[10px] text-gray-400">{title}</p>
    </div>
  </div>
);

const StatusBadge = ({ has }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
    has ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
  }`}>
    {has ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
  </span>
);

const FilterButton = ({ label, active, onClick, onClickNo }) => (
  <div className="flex rounded-lg overflow-hidden border border-white/10">
    <button
      onClick={onClick}
      className={`px-2 py-1 text-xs ${active === true ? 'bg-green-500/30 text-green-400' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
    >
      ✓ {label}
    </button>
    <button
      onClick={onClickNo}
      className={`px-2 py-1 text-xs border-l border-white/10 ${active === false ? 'bg-red-500/30 text-red-400' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
    >
      ✗
    </button>
  </div>
);

export default AdminVehicleMaster;
